/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { lookupProductByBarcode, searchProducts } from '../../catalog/infra-api'
import { preflightCatalogSnapshotForRead } from '../../catalog/infra-api'
import type { ProductSearchRow } from '../../catalog/domain-api';
import { getLastStockMovementUnitCost } from '../../inventory/public-api';
import { getErrorMessage } from '../../shared/errorMessage';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { CatalogStockProductSearchFields } from '../../shared/product-search/components/CatalogStockProductSearchFields'
import { ProductSearchPinnedChip } from '../../shared/product-search/components/ProductSearchPinnedChip';
import { BarcodeScannerModal } from '../../shared/ui/BarcodeScannerModal'
import { showError } from '../../shared/ui/appToast';
import { StyleSheet, Text } from 'react-native';
import { ticketColors } from '../../shared/theme/ticketColors';

function isPurchaseStockProduct(row: { itemType?: string | null; isMisc?: boolean | null }): boolean {
  return row.isMisc !== true && row.itemType !== 'service';
}

export type PickedPurchaseProduct = {
  id: string;
  name: string;
  sku: string | null;
  unitOfMeasure: string | null;
  supplierItemCode: string | null;
  lastUnitCost: number | null;
};

type Props = {
  branchId: string;
  selected: { id: string; name: string; sku?: string | null; supplierItemCode?: string | null } | null;
  disabled?: boolean;
  onSelect: (row: PickedPurchaseProduct) => void;
  onClear: () => void;
};

function toPicked(row: {
  id: string;
  name: string;
  sku?: string | null;
  unitOfMeasure?: string | null;
  supplierItemCode?: string | null;
}): Omit<PickedPurchaseProduct, 'lastUnitCost'> {
  return {
    id: row.id,
    name: row.name,
    sku: row.sku ?? null,
    unitOfMeasure: row.unitOfMeasure ?? null,
    supplierItemCode: row.supplierItemCode?.trim() ? row.supplierItemCode.trim() : null,
  };
}

export function PurchaseProductPicker({ branchId, selected, disabled, onSelect, onClear }: Props) {
  const [query, setQuery] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [catalogEpoch, setCatalogEpoch] = useState(0);
  const lastCostRequestRef = useRef(0);

  useEffect(() => {
    if (!branchId || selected) return;
    let cancelled = false;
    void preflightCatalogSnapshotForRead()
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setCatalogEpoch((n) => n + 1);
      });
    return () => {
      cancelled = true;
    };
  }, [branchId, selected]);

  const pickRow = useCallback(
    (
      row: ProductSearchRow | {
        id: string;
        name: string;
        sku?: string | null;
        unitOfMeasure?: string | null;
        itemType?: string | null;
        isMisc?: boolean | null;
        supplierItemCode?: string | null;
      },
    ) => {
      if (!isPurchaseStockProduct(row)) {
        showError('Eso no es mercadería', 'Compras usa productos de stock. Un servicio o Varios no entra en una orden.');
        return;
      }
      const picked = toPicked(row);
      const requestId = ++lastCostRequestRef.current;
      onSelect({ ...picked, lastUnitCost: null });
      void (async () => {
        let lastUnitCost: number | null = null;
        try {
          lastUnitCost = await getLastStockMovementUnitCost(row.id, branchId);
        } catch {
          lastUnitCost = null;
        }
        if (requestId !== lastCostRequestRef.current) return;
        onSelect({ ...picked, lastUnitCost });
      })();
      setQuery('');
    },
    [branchId, onSelect],
  );

  const searchPurchaseProducts = useCallback(
    (params: {
      search: string;
      limit: number;
      offset: number;
      branchId: string | null | undefined;
      posExcludeMisc?: boolean;
    }) => {
      void catalogEpoch;
      return searchProducts({
        search: params.search,
        limit: params.limit,
        offset: params.offset,
        branchId: params.branchId,
        posExcludeMisc: params.posExcludeMisc ?? true,
        inventoryPhysicalOnly: true,
      });
    },
    [catalogEpoch],
  );

  const handleScannedCode = useCallback(
    async (rawCode: string) => {
      setScannerOpen(false);
      try {
        const result = await lookupProductByBarcode({ code: rawCode, branchId, allowRemoteLookup: true });
        if (result.kind === 'local_hit') {
          pickRow(result.product);
          return;
        }
        setQuery(result.barcode);
        if (result.kind === 'offline_miss') {
          showError('Sin conexión', 'Ese código no está acá y no hay internet para buscarlo.');
          return;
        }
        showError('No está en el catálogo', 'Cargalo primero en Catálogo y volvé a escanear.');
      } catch (error: unknown) {
        presentUnexpectedFailure({ module: 'purchases', op: 'barcode_lookup', error, userMessage: 'No pudimos leer el código.' });
      }
    },
    [branchId, pickRow],
  );

  const scanner = (
    <BarcodeScannerModal
      visible={scannerOpen}
      title="Leer código"
      subtitle="Poné el código frente a la cámara."
      onClose={() => setScannerOpen(false)}
      onCodeScanned={(code) => {
        void handleScannedCode(code);
      }}
    />
  );

  if (selected) {
    return (
      <>
        <ProductSearchPinnedChip
          caption="Producto"
          productLabel={selected.sku ? `${selected.name} · ${selected.sku}` : selected.name}
          disabled={disabled}
          onClear={() => {
            setQuery('');
            onClear();
          }}
        />
        {selected.supplierItemCode ? (
          <Text style={styles.supplierCode}>Cód. proveedor: {selected.supplierItemCode}</Text>
        ) : null}
        {scanner}
      </>
    );
  }

  return (
    <>
      <CatalogStockProductSearchFields
        value={query}
        onChangeText={setQuery}
        branchId={branchId}
        searchProductsFn={searchPurchaseProducts}
        placeholder="Buscar por nombre o código…"
        showBarcodeCameraButton
        onPressScan={() => setScannerOpen(true)}
        sectionLabel={null}
        onPickSuggestion={(row) => pickRow(row)}
      />
      {scanner}
    </>
  );
}

const styles = StyleSheet.create({
  supplierCode: {
    fontSize: 12,
    color: ticketColors.ink3,
    marginTop: 4,
  },
});
